import {
  type PrintTaskCards,
  type SignerKind,
  WorkPackageClassificationService,
  WorkPackageLifecycleService,
  WorkPackageNumberSequenceService,
  WorkPackagePrintingService,
  WorkPackageService,
  WorkPackageSettingsService,
  type WorkPackageStatus,
  type WorkTaskPublic,
  WorkTaskService,
  WorkTaskSignOffService,
  type WorkTasksPublic,
} from "@common/client"
import { quoteBaseKey } from "./quoting/quote"
import { squawksBaseKey } from "./quoting/squawk"
import {
  makeAxiosMutation,
  makeGroupPages,
  makeInfiniteQuery,
  makeNoArgsQuery,
  makeQuery,
  parseBlobErrors,
} from "./utils"

export const workPackagesKey = ["work-packages"] as const

export const workPackageBaseKey = ["work-package"] as const

export const workPackageKey = (id: string) =>
  [...workPackageBaseKey, id] as const

export const workPackageSubjectsKey = ["work-package-subjects"] as const

export const workPackageQuery = makeQuery({
  serviceCall: WorkPackageService.readItem,
  queryKey: (params) => workPackageKey(params.id),
})

export const workPackageTasksQuery = makeQuery({
  serviceCall: WorkPackageService.readWithTasks,
  queryKey: (params) => [...workPackageKey(params.id), "tasks"],
})

export const workPackageRateCardsQuery = makeQuery({
  serviceCall: WorkPackageService.listRateCards,
  queryKey: (params) => [...workPackageKey(params.id), "rate-cards"],
})

export const workPackagesQuery = makeQuery({
  serviceCall: WorkPackageService.readItems,
  queryKey: (params) => [...workPackagesKey, "list", params],
})

const WORK_PACKAGES_PAGE_SIZE = 50

export type WorkPackagePagesParams = Omit<
  NonNullable<Parameters<typeof WorkPackageService.readItems>[0]>,
  "skip" | "limit"
>

export const workPackagesInfiniteQuery = makeInfiniteQuery({
  serviceCall: (params: WorkPackagePagesParams, page) =>
    WorkPackageService.readItems({ ...params, ...page }),
  queryKey: (params) => [...workPackagesKey, "pages", params],
  pageSize: WORK_PACKAGES_PAGE_SIZE,
})

export const workPackageGroupPages = makeGroupPages({
  serviceCall: (params: WorkPackagePagesParams, page) =>
    WorkPackageService.readItems({ ...params, ...page }),
  queryKey: (params) => [...workPackagesKey, "grouped", params],
  pageSize: WORK_PACKAGES_PAGE_SIZE,
})

export const workPackageSubjectsQuery = makeNoArgsQuery({
  serviceCall: WorkPackageService.subjects,
  queryKey: workPackageSubjectsKey,
})

export const useCreateWorkPackageMutation = makeAxiosMutation({
  serviceCall: WorkPackageService.createItem,
  invalidates: () => [workPackagesKey],
})

export const useUpdateWorkPackageMutation = makeAxiosMutation({
  serviceCall: WorkPackageService.updateItem,
  invalidates: (params) => [
    workPackagesKey,
    workPackageKey(params.id),
    squawksBaseKey,
  ],
})

export function changedWorkPackageKeys(id: string) {
  return [workPackagesKey, workPackageKey(id), squawksBaseKey]
}

export const useArchiveWorkPackageMutation = makeAxiosMutation({
  serviceCall: WorkPackageService.archiveItem,
  invalidates: (params) => changedWorkPackageKeys(params.id),
})

export const useRestoreWorkPackageMutation = makeAxiosMutation({
  serviceCall: WorkPackageService.restoreItem,
  invalidates: (params) => changedWorkPackageKeys(params.id),
})

export const useDeleteWorkPackageMutation = makeAxiosMutation({
  serviceCall: WorkPackageService.deleteItem,
  invalidates: (params) => changedWorkPackageKeys(params.id),
})

export const useChangeWorkPackageStatusMutation = makeAxiosMutation({
  serviceCall: WorkPackageLifecycleService.changeWorkPackageStatus,
  invalidates: (params) => changedWorkPackageKeys(params.work_package_id),
})

export const useCloseWorkPackageTasksMutation = makeAxiosMutation({
  serviceCall: WorkPackageLifecycleService.closeWorkPackageTasks,
  invalidates: (params) => changedWorkPackageKeys(params.work_package_id),
})

export const usePrintTaskCardsMutation = makeAxiosMutation({
  serviceCall: parseBlobErrors(
    (params: { work_package_id: string; printTaskCards: PrintTaskCards }) =>
      WorkPackagePrintingService.printTaskCards(params),
  ),
  invalidates: () => [],
})

export const usePrintWorkPackMutation = makeAxiosMutation({
  serviceCall: parseBlobErrors(
    ({ work_package_id }: { work_package_id: string }) =>
      WorkPackagePrintingService.printWorkPack({ work_package_id }),
  ),
  invalidates: () => [],
})

export const useCloseWorkPackageMutation = makeAxiosMutation({
  serviceCall: WorkPackageLifecycleService.closeWorkPackage,
  invalidates: (params) => changedWorkPackageKeys(params.work_package_id),
})

export const useCloseWorkPackagesMutation = makeAxiosMutation({
  serviceCall: WorkPackageLifecycleService.closeWorkPackages,
  invalidates: () => [workPackagesKey, workPackageBaseKey, squawksBaseKey],
})

export const useMarkWorkPackageAogMutation = makeAxiosMutation({
  serviceCall: WorkPackageLifecycleService.markWorkPackageAog,
  invalidates: (params) => changedWorkPackageKeys(params.work_package_id),
})

export const useClearWorkPackageAogMutation = makeAxiosMutation({
  serviceCall: WorkPackageLifecycleService.clearWorkPackageAog,
  invalidates: (params) => changedWorkPackageKeys(params.work_package_id),
})

export const workPackageNumberSequenceKey = [
  "work-package-number-sequence",
] as const

export const workPackageNumberSequenceQuery = makeNoArgsQuery({
  serviceCall: WorkPackageNumberSequenceService.getWorkPackageNumberSequence,
  queryKey: workPackageNumberSequenceKey,
})

export const useUpsertWorkPackageNumberSequenceMutation = makeAxiosMutation({
  serviceCall: WorkPackageNumberSequenceService.upsertWorkPackageNumberSequence,
  invalidates: () => [workPackageNumberSequenceKey],
})

export const workPackageSettingsKey = ["work-package-settings"] as const

export const workPackageSettingsQuery = makeNoArgsQuery({
  serviceCall: WorkPackageSettingsService.getWorkPackageSettings,
  queryKey: workPackageSettingsKey,
})

export const useUpsertWorkPackageSettingsMutation = makeAxiosMutation({
  serviceCall: WorkPackageSettingsService.upsertWorkPackageSettings,
  invalidates: () => [workPackageSettingsKey],
})

export const useSetWorkscopeMutation = makeAxiosMutation({
  serviceCall: WorkPackageClassificationService.setClassification,
  invalidates: (params) => [
    workPackagesKey,
    workPackageKey(params.work_package_id),
    quoteBaseKey,
  ],
})

function changedWorkTaskKeys(changed: WorkTasksPublic) {
  return [
    workPackagesKey,
    ...[
      ...new Set(changed.data.flatMap((task) => task.work_package_id ?? [])),
    ].map(workPackageKey),
  ]
}

export const useChangeWorkTaskStatusMutation = makeAxiosMutation({
  serviceCall: WorkTaskService.changeStatus,
  invalidates: (_, changed) => changedWorkTaskKeys(changed),
})

export const useAssignWorkTasksMutation = makeAxiosMutation({
  serviceCall: WorkTaskService.assign,
  invalidates: (_, changed) => changedWorkTaskKeys(changed),
})

type WorkPackageSubscriptionMutationInput = {
  id?: string
  work_package_number: string
}

export const useSubscribeWorkPackageMutation = makeAxiosMutation({
  serviceCall: ({
    work_package_number,
  }: WorkPackageSubscriptionMutationInput) =>
    WorkPackageService.subscribeToWorkPackage({ work_package_number }),
  invalidates: (params) => [
    workPackagesKey,
    ...(params.id ? [workPackageKey(params.id)] : []),
    squawksBaseKey,
  ],
})

export const useUnsubscribeWorkPackageMutation = makeAxiosMutation({
  serviceCall: ({
    work_package_number,
  }: WorkPackageSubscriptionMutationInput) =>
    WorkPackageService.unsubscribeFromWorkPackage({ work_package_number }),
  invalidates: (params) => [
    workPackagesKey,
    ...(params.id ? [workPackageKey(params.id)] : []),
    squawksBaseKey,
  ],
})

export const workPackageSearchBaseKey = [...workPackagesKey, "search"] as const

export const workPackageSearchKey = (params: {
  search?: string
  limit?: number
  status?: WorkPackageStatus[]
}) =>
  [
    ...workPackageSearchBaseKey,
    params.search,
    params.limit,
    params.status,
  ] as const

export const workPackageSearchQuery = makeQuery({
  serviceCall: WorkPackageService.readItems,
  queryKey: workPackageSearchKey,
})

export const workPackagesByCustomerKey = (customerId: string) =>
  [...workPackagesKey, "by-customer", customerId] as const

export const workPackagesByCustomerQuery = makeQuery({
  serviceCall: (params: { customer_id: string }) =>
    WorkPackageService.readItems({ customer_id: [params.customer_id] }),
  queryKey: (params: { customer_id: string }) =>
    workPackagesByCustomerKey(params.customer_id),
})

function signedTaskKeys(task: WorkTaskPublic) {
  return [
    workPackagesKey,
    ...(task.work_package_id ? [workPackageKey(task.work_package_id)] : []),
  ]
}

export const workTaskSignOffHistoryKey = (
  workPackageId: string,
  workTaskId: string,
) => [...workPackageKey(workPackageId), "sign-off-history", workTaskId] as const

export const workTaskSignOffHistoryQuery = makeQuery({
  serviceCall: (params: { work_package_id: string; work_task_id: string }) =>
    WorkTaskSignOffService.taskSignOffHistory({
      work_task_id: params.work_task_id,
    }),
  queryKey: (params: { work_package_id: string; work_task_id: string }) =>
    workTaskSignOffHistoryKey(params.work_package_id, params.work_task_id),
})

export const signersKey = (kind: SignerKind) =>
  ["work-task-signers", kind] as const

export const signersQuery = makeQuery({
  serviceCall: WorkTaskSignOffService.listSigners,
  queryKey: ({ kind }: { kind: SignerKind }) => signersKey(kind),
})

export const useSignOffWorkTaskMutation = makeAxiosMutation({
  serviceCall: WorkTaskSignOffService.signOffTask,
  invalidates: (_, task) => signedTaskKeys(task),
})

export const useSignOffWorkTasksMutation = makeAxiosMutation({
  serviceCall: ({
    work_package_id: _,
    ...params
  }: Parameters<typeof WorkTaskSignOffService.signOffTasks>[0] & {
    work_package_id: string
  }) => WorkTaskSignOffService.signOffTasks(params),
  invalidates: (params) => changedWorkPackageKeys(params.work_package_id),
})

export const useUndoWorkTaskSignOffMutation = makeAxiosMutation({
  serviceCall: WorkTaskSignOffService.undoTaskSignOff,
  invalidates: (_, task) => signedTaskKeys(task),
})

export const useInspectWorkTaskMutation = makeAxiosMutation({
  serviceCall: WorkTaskSignOffService.inspectTask,
  invalidates: (_, task) => signedTaskKeys(task),
})

export const useUndoWorkTaskInspectionMutation = makeAxiosMutation({
  serviceCall: WorkTaskSignOffService.undoTaskInspection,
  invalidates: (_, task) => signedTaskKeys(task),
})
